import { PromiseCompletionSource } from "scene-manager";
import { ICanvasScaler } from "../../../../../canvas/ICanvasScaler";
import { FullScreenFade } from "../../../../common/components/FullScreenFade";
import { IDestroyOptions, isMobile, Point, Sprite, Text, TextStyle } from "pixi.js";
import { VictorySceneResult } from "../VictorySceneResult";
import { CoinsCounter } from "../../../../common/components/CoinsCounter";
import { CoinsReward } from "../components/CoinsReward";
import { VegetablesReward } from "../components/VegetablesReward";
import { CommonResourcesPackage } from "../../../../common/resources/CommonResourcesPackage";
import { VictoryResourcesPackage } from "../resources/VictoryResourcesPackage";
import { LocalizationModel } from "../../../../common/models/LocalizationModel";
import { LocalizationKey } from "../../../../localization/Localization";
import { GameFont } from "../../../../common/GameFont";
import { PlayerModel } from "../../../../common/models/PlayerModel";
import { VictoryPopupController } from "../controllers/VictoryPopupController";
import { BoostersPurchasePanel } from "../components/BoostersPurchasePanel";
import { AudioController } from "../../../game/components/AudioController";
import { TypedText } from "../../../../common/components/TypedText";
import { AdaptiveViewDrop } from "../../../../common/views/AdaptiveViewDrop";
import { SpineResourcesPackage } from "../../../../common/resources/SpineResourcesPackage";
import { Spine } from "pixi-spine";
import { UIButtonBase } from "../../../../common/components/UIButtonBase";
import { AdaptiveText } from "ui-pixijs";
import { BoostersConfiguration } from "../../../../common/models/BoostersConfiguration";

export class VictoryView extends AdaptiveViewDrop<VictorySceneResult> {
    private fade: FullScreenFade;
    private coinsCounter: CoinsCounter;
    private header: Sprite;
    private headerText: TypedText;
    private coinsReward: CoinsReward;
    private vegetablesReward: VegetablesReward;
    private boostersBoxSubstrate: Sprite;
    private boostersShopText: AdaptiveText;
    private boostersBox: BoostersPurchasePanel;
    private homeButton: UIButtonBase;
    private adButton: UIButtonBase;
    private adButtonShine: Spine;
    private nextLevelButton: UIButtonBase;
    private confetti: Spine;

    public constructor(
        private readonly commonResources: CommonResourcesPackage,
        private readonly spineResources: SpineResourcesPackage,
        private readonly victoryResources: VictoryResourcesPackage,
        private readonly localizationModel: LocalizationModel,
        private readonly playerModel: PlayerModel,
        private readonly boostersConfiguration: BoostersConfiguration,
        private readonly victoryController: VictoryPopupController,
        private readonly audioController: AudioController,
        private coinsRewardValue: number,
        private savedVegetables: number,
        private totalVegetables: number,
        canvasScaler: ICanvasScaler,
        operationControls: PromiseCompletionSource<VictorySceneResult>
    ) {
        super(canvasScaler, operationControls);

        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight, true);

        this.homeButton.onUp.add(this.onHomeButtonClick, this);
        this.adButton.onUp.add(this.onAdButtonClick, this);
        this.nextLevelButton.onUp.add(this.onNextLevelButtonClick, this);
    }

    public override destroy(options?: IDestroyOptions | boolean): void {
        if (this.confetti) {
            this.confetti.state.clearListeners();
            if (this.confetti.parent) {
                this.removeChild(this.confetti);
            }
            this.confetti.destroy(options);
            this.confetti = null!;
        }
        if (this.adButtonShine) {
            this.adButtonShine.state.clearListeners();
            if (this.adButtonShine.parent) {
                this.removeChild(this.confetti);
            }
            this.adButtonShine.destroy(options);
            this.adButtonShine = null!;
        }
        this.homeButton.onUp.remove(this.onHomeButtonClick, this);
        this.adButton.onUp.remove(this.onAdButtonClick, this);
        this.nextLevelButton.onUp.remove(this.onNextLevelButtonClick, this);
        super.destroy(options);
    }

    public async start(): Promise<void> {
        await this.show();
        await this.headerText.animateText();
        if (this.confetti) {
            this.confetti.visible = true;
        }
        await this.coinsReward.animateContainer(new Point(33.5, 280), new Point(33.5, 53.5), 150, 1500);
        this.coinsReward.glow();
        await this.vegetablesReward.animateContainer(new Point(33.5, 230), new Point(33.5, 43.5), 150, 1500);
        this.vegetablesReward.glow();
    }

    protected override build(): void {
        this.cursor = "hover";
        this.fade = new FullScreenFade(this.canvasScaler, 0.8);
        this.coinsCounter = new CoinsCounter(this.playerModel, this.commonResources, this.spineResources);
        this.frame = new Sprite(this.victoryResources.assets_popups_victory_win_frame_png);
        this.frame.alpha = 0;

        this.header = new Sprite(this.victoryResources.assets_popups_victory_purple_ribbon_png);
        this.headerText = new TypedText(this.localizationModel, LocalizationKey.LevelCompleted, new HeaderTextStyle(), 738, 125);
        this.headerText.position.y = 31;
        this.header.addChild(this.headerText);
        this.headerText.updateText(false);

        this.coinsReward = new CoinsReward(this.commonResources, this.spineResources, this.victoryResources, this.coinsCounter, this, this.coinsRewardValue);
        this.vegetablesReward = new VegetablesReward(this.commonResources, this.spineResources, this.victoryResources, this.savedVegetables, this.totalVegetables);

        this.boostersBoxSubstrate = new Sprite(this.victoryResources.assets_popups_victory_shop_substrate_png);
        this.boostersShopText = new AdaptiveText(this.localizationModel.getText(LocalizationKey.BuyCallToAction), new ShopTextStyle(), 550, 125);
        this.boostersBox = new BoostersPurchasePanel(this.commonResources, this.victoryResources, this.playerModel, this.boostersConfiguration, this.victoryController, this.audioController);

        this.homeButton = new UIButtonBase({ defaultTexture: this.commonResources.assets_common_button_background_png });
        const homeIcon = new Sprite(this.commonResources.assets_common_home_icon_png);
        homeIcon.position.set(40, 31);
        this.homeButton.defaultView.addChild(homeIcon);

        this.adButtonShine = new Spine((this.spineResources.assets_spine_victory_ad_button_shine_Button_X5_json as any).spineData);
        this.adButtonShine.state.setAnimation(0, "Button_Anim", true);

        this.adButton = new UIButtonBase({ defaultTexture: this.victoryResources.assets_popups_victory_multiplier_ad_button_png });
        const adButtonMultiplier = new Text("x 5", new MultiplierAdTextStyle());
        adButtonMultiplier.position.set(126, 83);
        this.adButton.defaultView.addChild(adButtonMultiplier);

        this.nextLevelButton = new UIButtonBase({ defaultTexture: this.commonResources.assets_common_button_background_png });
        const nextLevelIcon = new Sprite(this.victoryResources.assets_popups_victory_next_level_icon_png);
        nextLevelIcon.position.set(77, 48);
        this.nextLevelButton.defaultView.addChild(nextLevelIcon);

        this.confetti = new Spine((this.spineResources.assets_spine_confetti_confetti_json as any).spineData);
        this.confetti.visible = false;
        this.confetti.state.setAnimation(0, isMobile.phone ? "confetti_mobil" : "confetti", false);
        this.confetti.state.addListener({
            complete: () => {
                requestAnimationFrame(() => {
                    if (this.confetti && this.confetti.parent) {
                        this.removeChild(this.confetti);
                    }
                    if (this.confetti) {
                        this.confetti.destroy();
                        this.confetti = null!;
                    }
                });
            }
        });

        this.boostersBoxSubstrate.addChild(this.boostersShopText, this.boostersBox);

        this.frame.addChild(
            this.header,
            this.coinsReward,
            this.vegetablesReward,
            this.boostersBoxSubstrate,
            this.homeButton,
            this.adButtonShine,
            this.adButton,
            this.nextLevelButton
        );

        this.addChild(
            this.fade,
            this.coinsCounter,
            this.confetti,
            this.frame
        );

        super.build();
    }

    protected layout(width: number, height: number, _initialLayout: boolean = false): void {
        switch (height) {
            case 2400:
                this.layout9x20(width, height);
                break;
            case 1620:
                this.layout10x9();
                break;
            case 1800:
                this.layout4x3();
                break;
        }


        this.header.position.set(-120, -57);
        this.coinsReward.position.set(379, 222.5);
        this.vegetablesReward.position.set(379, 350);
        this.boostersShopText.position.set((this.boostersBoxSubstrate.width - this.boostersShopText.width) * 0.5, 35);
        this.boostersBox.position.set((this.boostersBoxSubstrate.width - this.boostersBox.width) * 0.5, 110);
        this.boostersBoxSubstrate.position.set((this.frame.width - this.boostersBoxSubstrate.width) * 0.5, 430);
        this.homeButton.position.set(45, 780);
        this.adButtonShine.position.set(265 + this.adButton.width * 0.5, 790 + this.adButton.height * 0.5);
        this.adButton.position.set(265, 790);
        this.nextLevelButton.position.set(498, 780);

        this.circle?.position.set(width * 0.5, -40);
    }

    private layout9x20(width: number, height: number): void {
        this.coinsCounter.scale.set(0.85);
        this.coinsCounter.position.set(728.725 + this.coinsCounter.width * 0.5, 60 + this.coinsCounter.height * 0.5);
        this.frame.position.set((width - this.frame.width) * 0.5, (height - this.frame.height) * 0.5);
        this.confetti?.position.set(this.canvasScaler.referenceWidth * 0.5, this.frame.position.y + this.frame.height * 0.5);
    }

    private layout10x9(): void {
        this.coinsCounter.position.set(150 + this.coinsCounter.width * 0.5, 100 + this.coinsCounter.height * 0.5);
        this.frame.position.set(1061, 302);
        this.confetti?.position.set(this.canvasScaler.referenceWidth * 0.5, this.frame.position.y + this.frame.height * 0.5);
    }

    private layout4x3(): void {
        this.coinsCounter.position.set(150 + this.coinsCounter.width * 0.5, 100 + this.coinsCounter.height * 0.5);
        this.frame.position.set(821, 267);
        this.confetti?.position.set(this.canvasScaler.referenceWidth * 0.5, this.frame.position.y + this.frame.height * 0.5);
    }

    private async onHomeButtonClick(): Promise<void> {
        await this.victoryController.showMidrollAd();
        this.cancelAnimations();
        await this.hide();
        this.operationControls.complete(VictorySceneResult.Home);
    }

    private async onAdButtonClick(): Promise<void> {
        this.adButton.enabled = false;
        this.adButton.alpha = 0;
        this.adButtonShine.visible = false;
        if (this.adButtonShine && this.adButtonShine.parent) {
            this.removeChild(this.confetti);
        }
        if (this.confetti) {
            this.confetti.destroy();
            this.confetti = null!;
        }
        this.boostersBox.lockButtons();
        const isAdViewed = await this.victoryController.showAd();
        if (isAdViewed) {
            const [result, increment] = this.victoryController.applyCoinsAdMultiplier(this.coinsRewardValue);
            await this.coinsReward.playAdAnimation(result, increment);
        }
        this.boostersBox.unlockButtons();
    }

    private async onNextLevelButtonClick(): Promise<void> {
        await this.victoryController.showMidrollAd();
        this.cancelAnimations();
        await this.hide();
        this.operationControls.complete(VictorySceneResult.NextLevel);
    }

    private cancelAnimations(): void {
        this.coinsReward.cancelAnimations();
        this.coinsCounter.cancelAnimations();
    }

}

class HeaderTextStyle extends TextStyle {
    public constructor() {
        super({
            fontSize: 60,
            fill: "#FFFFFF",
            fontFamily: GameFont.Default,
            dropShadow: true,
            dropShadowColor: "#2B008C",
            dropShadowBlur: 0.5,
            dropShadowDistance: 4,
            dropShadowAngle: 1.77
        });
    }
}

class ShopTextStyle extends TextStyle {
    public constructor() {
        super({
            fontSize: 40,
            fill: "#FFFFFF",
            fontFamily: GameFont.Default,
            dropShadow: true,
            dropShadowColor: "#74007D",
            dropShadowBlur: 0.5,
            dropShadowDistance: 4,
            dropShadowAngle: 1.77
        });
    }
}

class MultiplierAdTextStyle extends TextStyle {
    public constructor() {
        super({
            fontSize: 32,
            fill: "#FFFFFF",
            fontFamily: GameFont.Default,
            dropShadow: true,
            dropShadowColor: "#00802D",
            dropShadowBlur: 0.5,
            dropShadowDistance: 4,
            dropShadowAngle: 1.77
        });
    }
}
